import { Check } from '@untitledui/icons'
import { isValidElement, useContext } from 'react'
import { ListBoxItem as AriaListBoxItem, Text as AriaText } from 'react-aria-components'

import { Avatar } from '@/lib/components/designSystem/Avatar'
import { SelectContext } from '@components/designSystem/select/context'
import { cx } from '@utils/cx.ts'
import { isReactComponent } from '@utils/is-react-component.ts'

import type { SelectItemType } from './select'
import type { ListBoxItemProps as AriaListBoxItemProps } from 'react-aria-components'

const styles = {
  sm: {
    root: 'p-2 pr-2.5',
    text: 'text-sm',
    supportingText: 'text-sm',
  },
  md: {
    root: 'p-2.5 pl-2',
    text: 'text-md',
    supportingText: 'text-md',
  },
}

interface SelectItemProps extends Omit<AriaListBoxItemProps<SelectItemType>, 'id'>, SelectItemType {}

export const SelectItem = ({ label, id, value, avatarUrl, supportingText, isDisabled, icon: Icon, className, children, ...props }: SelectItemProps) => {
  const { size } = useContext(SelectContext)

  const labelOrChildren = label ?? (typeof children === 'string' ? children : '')
  const textValue = supportingText ? labelOrChildren + ' ' + supportingText : labelOrChildren
  const hasHref = !!props.href

  return (
    <AriaListBoxItem
      id={id}
      value={
        value ?? {
          id,
          label: labelOrChildren,
          avatarUrl,
          supportingText,
          isDisabled,
          icon: Icon,
        }
      }
      textValue={textValue}
      isDisabled={isDisabled}
      {...props}
      className={(state) => cx(
        'w-full px-1.5 py-px outline-hidden',
        // when a 'href' prop is provided, AriaListBoxItem renders an anchor tag instead of a div
        // which handles padding differently than a div
        hasHref && 'block',
        typeof className === 'function' ? className(state) : className)
      }
    >
      {(state) => (
        <div
          className={cx(
            'flex cursor-pointer items-center gap-2 rounded-md outline-hidden select-none',
            state.isSelected && 'bg-active',
            state.isDisabled && 'cursor-not-allowed',
            state.isFocused && 'bg-primary_hover',
            state.isFocusVisible && 'ring-2 ring-focus-ring ring-inset',

            // Icon styles
            '*:data-icon:size-5 *:data-icon:shrink-0 *:data-icon:text-fg-quaternary',
            state.isDisabled && '*:data-icon:text-fg-disabled',

            styles[size].root
          )}
        >
          {avatarUrl ? (
            <Avatar aria-hidden="true" size="xs" src={avatarUrl} alt={label} />
          ) : isReactComponent(Icon) ? (
            <Icon data-icon aria-hidden="true" />
          ) : isValidElement(Icon) ? (
            Icon
          ) : null}

          <div className="flex w-full min-w-0 flex-1 flex-wrap gap-x-2">
            <AriaText
              slot="label"
              className={cx('truncate font-medium whitespace-nowrap text-primary', state.isDisabled && 'text-disabled', styles[size].text)}
            >
              {label ?? (typeof children === 'function' ? children(state) : children)}
            </AriaText>

            {supportingText && (
              <AriaText slot="description" className={cx('whitespace-nowrap text-tertiary', state.isDisabled && `
                text-disabled
              `, styles[size].supportingText)}>
                {supportingText}
              </AriaText>
            )}
          </div>

          {state.isSelected && (
            <Check
              aria-hidden="true"
              className={cx(
                'ml-auto text-fg-brand-primary',
                size === 'sm' ? 'size-4 stroke-[2.5px]' : 'size-5',
                state.isDisabled && 'text-fg-disabled'
              )}
            />
          )}
        </div>
      )}
    </AriaListBoxItem>
  )
}
